import React, { useCallback, useEffect, useState } from 'react';
import { ChevronRight, Wand2 } from 'lucide-react';

import { Button } from 'shadcn/components/ui/Button';
import { CardDescription } from 'shadcn/components/ui/Card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'shadcn/components/ui/Collapsible';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shadcn/components/ui/Select';
import { cn } from 'shadcn/lib/utils';

import { getExpirationFieldState } from '../resolveExpirationDefaults';

import { parseNonNegativeNumCases } from './palletBuilderUtils';

const NOOP = () => {};

/**
 * @typedef {{ id: number, code: string, displayName: string }} CompanySource
 *
 * @typedef {Object} QuickFillValues
 * @property {number} [companySourceId]
 * @property {number} [numCases]
 * @property {string} [expiration]
 * @property {string} [supplierLot]
 */

/**
 * @param {Object} props
 * @param {string} [props.applyButtonLabel]
 * @param {string} [props.className]
 * @param {boolean} [props.collapseOnApply]
 * @param {CompanySource[]} [props.companySourceOptions]
 * @param {boolean} [props.defaultOpen]
 * @param {import('../resolveExpirationDefaults').ExpirationDefaults} [props.expirationDefaults]
 * @param {string} [props.idPrefix]
 * @param {boolean} [props.isThirdPartyAsn]
 * @param {(values: QuickFillValues) => void} [props.onApply]
 * @param {number} [props.resetToken]
 * @param {boolean} [props.showDescription]
 */
function PalletQuickFillCollapsible({
    applyButtonLabel = 'Apply to all pallets',
    className,
    collapseOnApply = false,
    companySourceOptions = [],
    defaultOpen = false,
    expirationDefaults,
    idPrefix = 'quick-fill',
    isThirdPartyAsn = false,
    onApply = NOOP,
    resetToken,
    showDescription = true,
}) {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const [companySourceId, setCompanySourceId] = useState('');
    const [numCases, setNumCases] = useState('');
    const [expiration, setExpiration] = useState('');
    const [supplierLot, setSupplierLot] = useState('');

    const lotLabel = isThirdPartyAsn ? 'Partner Lot' : 'Supplier Lot';
    const {
        isLocked: isExpirationLocked,
        label: expirationLabel,
        showExpiration,
        value: expirationValue,
    } = getExpirationFieldState(expirationDefaults, expiration);

    const syncCompanySourceId = useCallback(() => {
        if (isThirdPartyAsn) {
            return;
        }

        if (companySourceOptions.length === 1) {
            setCompanySourceId(String(companySourceOptions[0].id));
            return;
        }

        setCompanySourceId((current) => {
            const isCurrentOptionValid = companySourceOptions.some((source) => String(source.id) === current);
            return isCurrentOptionValid ? current : '';
        });
    }, [companySourceOptions, isThirdPartyAsn]);

    const resetQuickFillFields = useCallback(() => {
        setNumCases('');
        setExpiration('');
        setSupplierLot('');

        if (isThirdPartyAsn) {
            return;
        }

        if (companySourceOptions.length === 1) {
            setCompanySourceId(String(companySourceOptions[0].id));
            return;
        }

        setCompanySourceId('');
    }, [companySourceOptions, isThirdPartyAsn]);

    useEffect(() => {
        syncCompanySourceId();
    }, [syncCompanySourceId]);

    useEffect(() => {
        if (resetToken === undefined || resetToken === 0) {
            return;
        }

        resetQuickFillFields();
        setIsOpen(false);
    }, [resetQuickFillFields, resetToken]);

    const handleApply = useCallback(() => {
        /** @type {QuickFillValues} */
        const values = {};

        if (companySourceId) {
            values.companySourceId = Number(companySourceId);
        }
        const parsedNumCases = parseNonNegativeNumCases(numCases);
        if (parsedNumCases !== undefined) {
            values.numCases = parsedNumCases;
        }
        if (expirationValue) {
            values.expiration = expirationValue;
        }
        if (supplierLot.trim()) {
            values.supplierLot = supplierLot.trim();
        }

        onApply(values);
        resetQuickFillFields();

        if (collapseOnApply) {
            setIsOpen(false);
        }
    }, [collapseOnApply, companySourceId, expirationValue, numCases, onApply, resetQuickFillFields, supplierLot]);

    return (
        <div className={className}>
            <Collapsible open={isOpen} onOpenChange={setIsOpen}>
                <div className={cn('px-3', isOpen ? 'pt-2' : 'py-1')}>
                    <CollapsibleTrigger asChild>
                        <button
                            type="button"
                            className="flex h-6 w-full items-center gap-1.5 rounded-sm text-left transition-colors hover:bg-accent/50"
                        >
                            <ChevronRight
                                aria-hidden="true"
                                className={cn(
                                    'size-3.5 shrink-0 text-muted-foreground transition-transform duration-200',
                                    isOpen && 'rotate-90',
                                )}
                            />
                            <Wand2 aria-hidden="true" className="size-3.5 shrink-0 text-[#4C884B]" />
                            <span className="text-sm leading-none font-semibold">Quick fill</span>
                        </button>
                    </CollapsibleTrigger>
                </div>
                <CollapsibleContent>
                    {showDescription ? (
                        <CardDescription className="px-3 pb-2 text-xs text-muted-foreground">
                            For simple receipts, set the shared values once and apply them. Only filled fields are
                            applied.
                        </CardDescription>
                    ) : null}
                    <div className="px-3 pt-0 pb-3">
                        <div className="flex flex-wrap items-end gap-2">
                            {!isThirdPartyAsn ? (
                                <div className="flex min-w-[120px] flex-[1.2] flex-col gap-1">
                                    <Label
                                        className="text-xs leading-none text-muted-foreground"
                                        htmlFor={`${idPrefix}-source`}
                                    >
                                        Source
                                    </Label>
                                    <Select value={companySourceId} onValueChange={setCompanySourceId}>
                                        <SelectTrigger id={`${idPrefix}-source`} className="w-full" size="sm">
                                            <SelectValue placeholder="Select" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {companySourceOptions.map((source) => (
                                                <SelectItem key={source.id} value={String(source.id)}>
                                                    {source.displayName}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            ) : null}
                            <div className="flex min-w-[72px] flex-[0.6] flex-col gap-1">
                                <Label
                                    className="text-xs leading-none text-muted-foreground"
                                    htmlFor={`${idPrefix}-num-cases`}
                                >
                                    Cases
                                </Label>
                                <Input
                                    id={`${idPrefix}-num-cases`}
                                    className="h-8 text-sm"
                                    min={0}
                                    type="number"
                                    onWheel={(e) => {
                                        e.target.blur();
                                    }}
                                    value={numCases}
                                    onChange={(event) => {
                                        const raw = event.target.value;
                                        if (raw === '') {
                                            setNumCases('');
                                            return;
                                        }
                                        const parsed = Number(raw);
                                        if (!Number.isFinite(parsed) || parsed < 0) {
                                            return;
                                        }
                                        setNumCases(raw);
                                    }}
                                />
                            </div>
                            {showExpiration ? (
                                <div className="flex min-w-[120px] flex-1 flex-col gap-1">
                                    <Label
                                        className="text-xs leading-none text-muted-foreground"
                                        htmlFor={`${idPrefix}-expiration`}
                                    >
                                        {expirationLabel}
                                    </Label>
                                    <Input
                                        id={`${idPrefix}-expiration`}
                                        className="h-8 text-sm"
                                        disabled={isExpirationLocked}
                                        type="date"
                                        value={expirationValue}
                                        onChange={(event) => setExpiration(event.target.value)}
                                    />
                                </div>
                            ) : null}
                            <div className="flex min-w-[100px] flex-1 flex-col gap-1">
                                <Label
                                    className="text-xs leading-none text-muted-foreground"
                                    htmlFor={`${idPrefix}-lot`}
                                >
                                    {lotLabel}
                                </Label>
                                <Input
                                    id={`${idPrefix}-lot`}
                                    className="h-8 text-sm"
                                    placeholder="Lot #"
                                    value={supplierLot}
                                    onChange={(event) => setSupplierLot(event.target.value)}
                                />
                            </div>
                            <Button
                                className="h-8 shrink-0 bg-[#4C884B] px-3 text-xs text-white hover:bg-[#4C884B]/90"
                                size="sm"
                                type="button"
                                onClick={handleApply}
                            >
                                {applyButtonLabel}
                            </Button>
                        </div>
                    </div>
                </CollapsibleContent>
            </Collapsible>
        </div>
    );
}

export { NOOP, PalletQuickFillCollapsible };
